import { useState, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

export interface Empresa {
  id_empresa: string;
  empresa: string;
  logo: string | null;
  descripcion?: string | null;
  telefono?: string | null;
  email?: string | null;
  reservas_online_citas?: boolean;
  plan_incluye_reservas?: boolean;
  reservado_previamente?: boolean;
  latitud?: number | null;
  longitud?: number | null;
  distancia?: number | null;
  imagenes?: string[];
  ultima_reserva_global?: string | null;
  ultima_reserva_usuario?: string | null;
  valoracion_media_empresa?: number | null;
  total_valoraciones?: number | null;
  total_reservas_usuario?: number | null;
  categoria_slug: string;
  ciudad_slug: string;
  empresa_slug: string;
  categoria?: string;
  ciudad?: string;
  servicios?: string[];
  subcategorias?: string[];
}

const RENOTIFY_MS = 30 * 24 * 60 * 60 * 1000; // 30 días

export const useEmpresasExplore = () => {
  const [empresasBase, setEmpresasBase] = useState<Empresa[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [errorEmpresas, setErrorEmpresas] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);

  // Puede ser null si el usuario no está logueado — es válido
  const userEmail = localStorage.getItem('email');

  const fetchEmpresasUsuario = useCallback(async () => {
    setLoadingEmpresas(true);
    setErrorEmpresas(null);

    const { data, error } = await supabase.rpc('get_empresas_cliente', {
      p_email: userEmail ?? null,
    });

    if (error) {
      Sentry.captureException(error, { extra: { userEmail } });
      setErrorEmpresas(error.message || 'Error al obtener empresas');
    } else {
      setEmpresasBase((data as Empresa[]) ?? []);
    }

    setLoadingEmpresas(false);
  }, [userEmail]);

  // Cargar ubicación guardada o pedir permiso
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    const lastAsked = localStorage.getItem('locationAskedAt');

    if (savedLocation) {
      setUserLocation(JSON.parse(savedLocation));
    }

    if (!lastAsked || Date.now() - Number(lastAsked) > RENOTIFY_MS) {
      setShowLocationDialog(true);
    }
  }, []);

  // Permitir ubicación
  const handleAllowLocation = () => {
    localStorage.setItem('locationAskedAt', Date.now().toString());
    setShowLocationDialog(false);

    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setUserLocation(coords);
        localStorage.setItem('userLocation', JSON.stringify(coords));
      },
      (err) => {
        Sentry.captureException(err, { extra: { type: 'geolocation' } });
      }
    );
  };

  // Rechazar ubicación
  const handleRejectLocation = () => {
    localStorage.setItem('locationAskedAt', Date.now().toString());
    setShowLocationDialog(false);
  };

  // Pedir la ubicación a petición del usuario (filtro "Cerca de mí").
  const solicitarUbicacion = () => {
    if (userLocation) return;
    setShowLocationDialog(true);
  };

  // Distancia y orden por defecto:
  //   1. Empresas donde el usuario ya ha reservado, las más reservadas primero.
  //   2. El resto por valoración media, desempatando por nº de valoraciones.
  //   3. Distancia y nombre como último criterio.
  // Es un memo y no un efecto que reescribe su propio estado: se recalcula
  // solo cuando cambian las empresas o la ubicación.
  const empresasUsuario = useMemo(() => {
    const lista = empresasBase.map((e) => ({
      ...e,
      distancia:
        userLocation && e.latitud != null && e.longitud != null
          ? getDistanceKm(userLocation.lat, userLocation.lon, e.latitud, e.longitud)
          : null,
    }));

    lista.sort((a, b) => {
      // Reservas del usuario: si la RPC aún no devuelve el contador, el
      // booleano vale como 1 para no perder el orden.
      const rA = Number(a.total_reservas_usuario ?? (a.reservado_previamente ? 1 : 0));
      const rB = Number(b.total_reservas_usuario ?? (b.reservado_previamente ? 1 : 0));
      if (rA !== rB) return rB - rA;

      const mA = Number(a.valoracion_media_empresa ?? 0);
      const mB = Number(b.valoracion_media_empresa ?? 0);
      if (mA !== mB) return mB - mA;

      const vA = Number(a.total_valoraciones ?? 0);
      const vB = Number(b.total_valoraciones ?? 0);
      if (vA !== vB) return vB - vA;

      if (a.distancia != null && b.distancia != null && a.distancia !== b.distancia)
        return a.distancia - b.distancia;
      return a.empresa.localeCompare(b.empresa);
    });

    return lista;
  }, [empresasBase, userLocation]);

  useEffect(() => {
    fetchEmpresasUsuario();
  }, [fetchEmpresasUsuario]);

  return {
    empresasUsuario,
    loadingEmpresas,
    errorEmpresas,
    refetchEmpresasUsuario: fetchEmpresasUsuario,
    userLocation,
    showLocationDialog,
    solicitarUbicacion,
    handleAllowLocation,
    handleRejectLocation,
  };
};

// Haversine
const getDistanceKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
};

const deg2rad = (deg: number) => deg * (Math.PI / 180);